import React, { useState, useEffect } from 'react';
import { Tooltip } from '@mui/material';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';

export default function ViewInventory() {
    const navigate = useNavigate();
    const location = useLocation();

    const inventoryId = location.state?.id;

    const [inventoryData, setInventoryData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [labData, setLabData] = useState([]); // State to store all labs

    // Fetch all labs on component mount
    useEffect(() => {
        fetchLabs();
    }, []);

    const fetchLabs = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('https://api.nvcti.in/labs/getAllLabs', {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            setLabData(response.data.data);
        } catch (error) {
            toast.error('Failed to fetch labs');
        }
    };

    useEffect(() => {
        if (!inventoryId) {
            toast.error('No inventory ID provided for viewing.');
            navigate('/admin/inventory');
            return;
        }
        fetchInventoryData(inventoryId);
    }, [inventoryId, navigate]);

    const fetchInventoryData = async (id) => {
        try {
            setLoading(true);
            setError(null);

            const token = localStorage.getItem('token');
            // const response = await axios.get(`https://api.nvcti.in/api/inventory/getInventoryById/${id}`, {
            const response = await axios.get(`https://api.nvcti.in/api/inventory/getInventoryById/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            setInventoryData(response.data);
        } catch (err) {
            console.error('Error fetching inventory data:', err);
            setError('Failed to load inventory details. Please try again.');
            toast.error('Failed to load inventory details.');
        } finally {
            setLoading(false);
        }
    };

    // Helper function to get lab name from lab ID
    const getLabName = (labId) => {
        const lab = labData.find(lab => lab.lab_id === labId);
        return lab ? lab.lab_name : 'Unknown Lab';
    };


    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <p className="text-blueGray-600">Loading inventory details...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex justify-center items-center h-screen">
                <p className="text-red-500">{error}</p>
                <button
                    className="ml-4 text-white bg-blueGray-700 active:bg-blueGray-600 font-semibold text-sm px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none"
                    onClick={() => navigate('/admin/inventory')}
                >
                    Go Back
                </button>
            </div>
        );
    }

    if (!inventoryData) {
        return null;
    }

    return (
        <>
            <Toaster position="bottom-left" reverseOrder={false} />
            <div className="flex justify-center items-center">
                <div className="relative flex flex-col min-w-0 break-words w-full mb-6 mt-6 shadow-lg rounded-lg bg-dark-secondary border-0">
                    <div className="rounded-t bg-dark-tertiary mb-0 px-6 py-6">
                        <div className="text-center flex justify-between">
                            <h6 className="text-white text-xl font-bold">View Inventory Details</h6>
                        </div>
                    </div>
                    <div className="flex-auto px-4 lg:px-10 py-10 pt-0">
                        <h6 className="text-gray-400 text-1xl mt-3 mb-6 font-bold">Inventory Information</h6>
                        <div className="flex flex-wrap">
                            <div className="w-full lg:w-6/12 px-4">
                                <div className="relative w-full mb-3">
                                    <label className="block text-gray-300 text-xs font-bold mb-2">Inventory Name</label>
                                    <p className="px-3 py-3 text-white bg-dark-primary rounded text-sm shadow-sm">
                                        {inventoryData.name}
                                    </p>
                                </div>
                            </div>
                            {/* <div className="w-full lg:w-6/12 px-4">
                                <div className="relative w-full mb-3">
                                    <label className="block text-blueGray-600 text-xs font-bold mb-2">Quantity</label>
                                    <p className="px-3 py-3 text-blueGray-700 bg-white rounded text-sm shadow-sm">
                                        {inventoryData.quantity}
                                    </p>
                                </div>
                            </div> */}
                            <div className="w-full lg:w-6/12 px-4">
                                <div className="relative w-full mb-3">
                                    <label className="block text-gray-300 text-xs font-bold mb-2">Lab Name</label>
                                    <p className="px-3 py-3 text-white bg-dark-primary rounded text-sm shadow-sm">
                                        {/* Use the getLabName helper function here */}
                                        {getLabName(inventoryData.lab_id)}
                                    </p>
                                </div>
                            </div>
                            {/* Display image if available, assuming inventory_image is a field */}
                            {inventoryData.inventory_image && (
                                <div className="w-full lg:w-6/12 px-4">
                                    <div className="relative w-full mb-3">
                                        <label className="block text-gray-300 text-xs font-bold mb-2">Inventory Image</label>
                                        <img
                                            src={inventoryData.inventory_image}
                                            alt="Inventory"
                                            className="w-full h-48 object-cover rounded shadow-sm"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                        <hr className="mt-6 border-b-1 border-blueGray-300" />
                        <h6 className="text-gray-400 text-sm mt-3 mb-6 font-bold">Description</h6>
                        <div className="flex flex-wrap">
                            <div className="w-full lg:w-12/12 px-4">
                                <div className="relative w-full mb-3">
                                    <p className="px-3 py-3 text-white bg-dark-primary rounded text-sm shadow-sm">
                                        {inventoryData.description || 'No description provided.'}
                                    </p>
                                </div>
                            </div>
                            <div className="flex justify-end w-full">
                                <Tooltip title="Back to Inventory" arrow>
                                    <button
                                        type="button"
                                        onClick={() => navigate('/admin/inventory')}
                                        className="mt-10 text-white bg-blueGray-700 active:bg-blueGray-600 font-semibold text-sm px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-auto ease-linear transition-all duration-150"
                                    >
                                        {'<< '}Back
                                    </button>
                                </Tooltip>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}